import { theme } from 'antd';
import { ATRIBUTO_DE_MATERIAL_AUTORAL } from '@titularis/analytics';
import './conteudo-rico.css';

/**
 * Conteúdo vindo da API.
 *
 * O HTML é gerado e **sanitizado no servidor**, no momento da publicação: o app
 * não carrega editor nem sanitizador. É por isso que injetar aqui é seguro - e
 * é a razão de a sanitização estar no backend, com fixtures congeladas, em vez
 * de depender de cada tela lembrar de escapar.
 */
/**
 * Prefixo da citação interna, no `href`.
 *
 * O mesmo do `content.ts` do backend, que já remove o `target` desses links -
 * então, mesmo que a interceptação abaixo falhe, o clique não abre aba: ele
 * apenas não faz nada. Falhar sem estragar é o mínimo que se pede de um recurso
 * que vive dentro do conteúdo.
 */
export const CITACAO_INTERNA = '#comentario:';

export function ConteudoRico({
  html,
  onCitacao,
  protegido = false,
}: {
  html: string | null | undefined;
  /** Chamado quando o aluno clica numa citação interna, com a aba de destino. */
  onCitacao?: (aba: string) => void;
  /**
   * Material autoral: bloqueia a seleção e a cópia.
   *
   * **Isto é atrito, não controle de acesso** (D-53), e vale dizer por escrito
   * para ninguém se apoiar nisto depois. O texto continua na resposta da API, a
   * uma aba de rede de distância, e devtools ou JavaScript desligado passam por
   * cima em dois cliques. Serve contra o Ctrl+C distraído, que é o caso comum.
   * Quem insiste é respondido pela marca do leitor, que o servidor põe no HTML
   * entregue - essa, sim, é a defesa.
   *
   * **Fora do enunciado, de propósito:** o enunciado é da banca, o aluno o
   * copia para buscar jurisprudência, e travá-lo atrapalharia quem não fez
   * nada para proteger o que não é nosso.
   *
   * **Também tira o bloco da gravação de sessão** (22/09/2026), e esta parte é
   * controle de verdade: o conteúdo não chega ao PostHog, porque o gravador o
   * substitui por um retângulo vazio antes de enviar. A linha é a mesma - o
   * enunciado aparece no replay, o comentário não.
   */
  protegido?: boolean;
}) {
  const { token } = theme.useToken();

  if (!html) return null;

  /*
   * Delegado no contêiner, e não link a link: o HTML vem pronto do servidor e
   * não há componente por âncora onde pendurar um handler.
   */
  const aoClicar = (evento: React.MouseEvent<HTMLDivElement>) => {
    const alvo = (evento.target as HTMLElement).closest('a');
    const href = alvo?.getAttribute('href');
    if (!href?.startsWith(CITACAO_INTERNA)) return;

    // Sem `preventDefault` o navegador salta para uma âncora inexistente e
    // ainda suja a URL.
    evento.preventDefault();
    onCitacao?.(href.slice(CITACAO_INTERNA.length));
  };

  return (
    <div
      onClick={aoClicar}
      /*
       * O evento de cópia é cancelado além do `user-select`: alguns navegadores
       * ainda copiam por Ctrl+A o que a seleção visual não pega.
       */
      onCopy={protegido ? (evento) => evento.preventDefault() : undefined}
      className={protegido ? 'titularis-conteudo titularis-conteudo--protegido' : 'titularis-conteudo'}
      {...(protegido ? { [ATRIBUTO_DE_MATERIAL_AUTORAL]: '' } : {})}
      style={
        {
          '--conteudo-cor-borda': token.colorBorderSecondary,
          '--conteudo-cor-secundaria': token.colorTextSecondary,
          '--conteudo-cor-cabecalho': token.colorFillTertiary,
        } as React.CSSProperties
      }
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
